
import { useEffect, useState, useContext } from "react";
import { FiArrowLeft, FiMenu, FiSettings } from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import classNames from "utilities/ClassNames";
import { DateTime, Info, UserMenu, LicenseAgreement } from "processhub-components-lib";
import SideMenu from "components/shared/SideMenu";
import ProgressMonitoringLogo from "components/shared/ProgressMonitoringLogo";
import useScreen from "hooks/useScreen";
import { TfiAgenda } from "react-icons/tfi";
import { ReactComponent as GanttChart } from "assets/ganttChart.svg";
import { ReactComponent as TimelineLogo } from "assets/timeline.svg";
import { ReactComponent as AiChatIcon } from "assets/aiChatIcon.svg";
import { ReactComponent as MapIcon } from "assets/mapIcon.svg";
import { ModalContext } from "hooks/ModalProvider";
import HeaderMenu from "./HeaderMenu";
import { ReactComponent as HealthsensorIcon } from "assets/healthsensorIcon.svg";
import useUnity from "hooks/UseUnity";
import allHooks from "hooks";
import { navLinks } from "../config/navLinksConfig";
import { profileConfig } from "../config/userMenuConfig";
import { useAuth } from "react-oidc-context";
import OverallProgress from "components/OverallProgress";
import { motion } from 'framer-motion';
import { useMinifiedMenu } from "hooks/useMinifiedMenu";
import { getCurrentSolution } from "api/solutions";
import { getOrganization } from "api/organizations";
import { getExtractedPath } from "utilities/extractedPath";
import extractUUIDs from "../utils/extractUUIDs";
import { getSlideProjectName } from "../config/animations";
import { FiX } from "react-icons/fi";
import { useAiChat } from "hooks/AiChatContext";
import { useUnityActions } from "hooks/unityActions";
import CharacterMenu from "components/CharacterMenu/CharacterMenu";
import useApp from "hooks/useApp";
import licenseAgreementConfig from "config/license/licenseAgreementConfig";

export default function Header() {
  const { modalType, setModalType, showKeyGuide, setShowKeyGuide, setShowHealth } = useContext(ModalContext);
  const { pathname } = useLocation();
  const [showSideMenu, setShowMenu] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const { aiChatShown, setAiChatShown } = useAiChat();

  const {
    setMinifiedMenuOpen
  } = useMinifiedMenu();

  const [ganttShown, setGanttShown] = useState(false);
  const [reportShown, setReportShown] = useState(false);
  const [settingsShown, setSettingsShown] = useState(false);
  const [mapShown, setMapShown] = useState(false);
  const linkTarget = "_self";
  const { isScaled } = useScreen();
  const { setUser } = useApp();
  const { TimelineShown, setTimelineShown } = useUnity();
  const { screenMode, setIsMovementDisabled } = useUnityActions();
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const auth = useAuth();
  const logout = async () => auth.signoutRedirect();
  const baseUrl = getExtractedPath();
  const { currOrg, curSol } = extractUUIDs(baseUrl);
  const [unityExit, setUnityExit] = useState(false);
  const [showCharacterHasChanged, setShowCharacterHasChanged] = useState(false);
  const is3DDashboard = location.pathname.endsWith("/3d-dashboard");
  const isProjectsPage = pathname.includes("/projects");
  const [showLicense, setShowLicense] = useState(false);
  const slideProjectName = getSlideProjectName();

  const onLicenseAccept = () => {
    setShowLicense(false);
  };

  const getProjectInfo = async () => {
    const response = await getCurrentSolution(currOrg, curSol);
    if (response.status === 200) {
      return response.data.data.name;
    }
    return '';
  };

  const getOrganizationInfo = async () => {
    const response = await getOrganization(currOrg);
    if (response.status === 200) {
      return response.data.data.name;
    }
    return '';
  };

  useEffect(() => {
    showSideMenu ? setIsMovementDisabled(1) : setIsMovementDisabled(0);
  }, [showSideMenu]);

  useEffect(() => {
    const fetchProjectName = async () => {
      const userProjectName = await getProjectInfo();
      setProjectName(userProjectName);
      const userOrganizationName = await getOrganizationInfo();
      setOrganizationName(userOrganizationName);
    };

    fetchProjectName();
  }, []);

  useEffect(() => {
    if (window.vuplex) {
      if (showSideMenu) {
        const message = JSON.stringify({ object: "__S:ReactInputBlocker__", type: "ReactInputBlocker", method: "SetBlockState", arguments: [1] });
        window.vuplex.postMessage(message);
      }
      if (!showSideMenu) {
        const message = JSON.stringify({ object: "__S:ReactInputBlocker__", type: "ReactInputBlocker", method: "SetBlockState", arguments: [0] });
        window.vuplex.postMessage(message);
      }
    }
  }, [showSideMenu]);

  useEffect(() => {
    if (modalType === 'report') {
      setReportShown(true);
      setSettingsShown(false);
      setGanttShown(false);
      setMapShown(false);
    }
    if (modalType === 'settings') {
      setSettingsShown(true);
      setReportShown(false);
      setGanttShown(false);
      setMapShown(false);
    }
    if (modalType === 'gantt') {
      setGanttShown(true);
      setReportShown(false);
      setSettingsShown(false);
      setMapShown(false);
    }
    if (modalType === 'map') {
      setMapShown(true);
      setReportShown(false);
      setSettingsShown(false);
      setGanttShown(false);
    }
    if (modalType === '') {
      setReportShown(false);
      setSettingsShown(false);
      setGanttShown(false);
      setMapShown(false);
    }
  }, [modalType]);

  useEffect(() => {
    if (pathname === "/3d-dashboard" && window.vuplex) {
      setTimeout(() => {
        setTimelineShown(true);
      }, 100);
    }
  }, [pathname]);

  const headerMenuButtonsConfig = [{
    title: 'Licence Agreement',
    action: () => setShowLicense(true)
  }];

  const buttonsConfig = [
    {
      name: 'AI ChatBot',
      shown: aiChatShown,
      type: 'component',
      onClick: () => setAiChatShown((prev: any) => !prev),
      Icon: AiChatIcon,
      activeClass: "headerIconActive",
      inactiveClass: "headerIconInactive"
    },
    {
      name: 'Timeline',
      shown: TimelineShown,
      type: 'component',
      onClick: () => setTimelineShown((prev: any) => !prev),
      Icon: TimelineLogo,
      activeClass: "headerIconActive",
      inactiveClass: "headerIconInactive"
    },
    {
      name: 'Gantt Chart',
      shown: ganttShown,
      type: 'component',
      onClick: () => {
        setModalType('gantt');
        setMinifiedMenuOpen(false);
      },
      Icon: GanttChart,
      activeClass: "headerIconActive",
      inactiveClass: "headerIconInactive"
    },
    {
      name: 'Report',
      shown: reportShown,
      type: 'icon',
      onClick: () => {
        setModalType('report');
        setMinifiedMenuOpen(false);
      },
      Icon: TfiAgenda,
      activeClass: "text-blue",
      inactiveClass: "hover:text-blue"
    },
    {
      name: 'Map',
      shown: mapShown,
      type: 'component',
      onClick: () => {
        setModalType('map');
        setMinifiedMenuOpen(false);
      },
      Icon: MapIcon,
      activeClass: "headerIconActive",
      inactiveClass: "headerIconInactive"
    },
    {
      name: 'Settings',
      shown: settingsShown,
      type: 'icon',
      onClick: () => {
        setModalType('settings');
        setMinifiedMenuOpen(false);
      },
      Icon: FiSettings,
      activeClass: "text-blue",
      inactiveClass: "hover:text-blue"
    },
  ];

  const onUnityClose = () => {
    const message = JSON.stringify(
      {
        object: "__ApplicationManager__",
        type: "ApplicationManager",
        method: "Quit",
        arguments: []
      });
    window.vuplex.postMessage(message);
  };

  const onCharacterConfirm = (name: string) => {
    setShowCharacterHasChanged(true);
    setUser((prev: any) => ({
      ...prev,
      characterName: name
    }));
  };


  useEffect(() => {
    if (window.vuplex) {
      addMessageListener();
    }
    else {
      window.addEventListener('vuplexready', addMessageListener);
    }
  }, []);

  useEffect(() => {
    if (screenMode !== 2) {
      setUnityExit(true);
    }
  }, [screenMode]);

  function addMessageListener() {
    window.vuplex.addEventListener("message", async function (event: { data: any }) {
      const tjson = event.data;
      const parsedInfo = JSON.parse(tjson);
      if (parsedInfo.methodName === 'SetExitButtonVisibility') {
        const showClose = JSON.parse(parsedInfo.message);
        setUnityExit(showClose);
      }
    });
  }

  return (
    <div className="w-full bg-dark relative z-40">
      <div
        className={classNames(
          "lg:h-12 xl-2xl:h-14 2xl:h-16",
          "mx-auto flex items-center px-3"
        )}
      >
        <div className={classNames(isScaled ? 'gap-3' : 'gap-2', "relative flex items-center w-full")}>
          <div
            onClick={() => setShowMenu(!showSideMenu)}
            className="cursor-pointer duration-300 z-50"
          >
            {!showSideMenu && <FiMenu size={"40"} className="lg:w-[80%] xl-2xl:w-[90%] 2xl:w-full" />}
            {showSideMenu && <FiArrowLeft size={"40"} className="lg:w-[80%] xl-2xl:w-[90%] 2xl:w-full" />}
          </div>
          <a
            href={baseUrl}
            {...(is3DDashboard
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            <ProgressMonitoringLogo variant="regular" />
          </a>
          <div className="w-full flex items-center">
            <motion.div
              initial="hidden"
              animate={showSideMenu ? "visible" : "hidden"}
              variants={slideProjectName}
              className="mt-1"
            >
              {projectName && (
                <span className="text-md 2xl:text-xl flex">
                  {/* {isProjectsPage && organizationName && ( */}
                    <span className="mr-2">{organizationName} | </span>
                  {/* )} */}
                  {projectName}
                </span>
              )}
            </motion.div>
          
            {!isProjectsPage && (
              <div className="w-full max-w-[260px] 2xl:max-w-[380px] px-2 mx-auto">
                <OverallProgress />
              </div>
            )}
          </div>
        </div>
        {!isProjectsPage && <HeaderMenu buttonsConfig={buttonsConfig} />}
        <div className="flex items-center mr-4">
          <DateTime hooks={allHooks} />
          <div className="hover:text-blue cursor-pointer w-max mr-6" onClick={() => setShowHealth(true)}>
            <HealthsensorIcon className={classNames("h-[23px] w-[23px] border-white rounded-full hover:text-blue")} />
          </div>
          <div className="flex items-center justify-center min-w-[40px]">
            <Info
              setShowKeyGuide={setShowKeyGuide}
              showKeyGuide={showKeyGuide}
              hooks={allHooks}
              iconSize={28}
            />
          </div>
        </div>
        <UserMenu profileConfig={profileConfig} headerMenuButtons={headerMenuButtonsConfig} hooks={allHooks} logout={logout} showProfileMenu={showProfileMenu} setShowProfileMenu={setShowProfileMenu} >
          <div className="mx-auto">
            <div className="z-50 bg-dark w-max h-max rounded-xl relative">
              <CharacterMenu onCharacterConfirm={onCharacterConfirm} />
              {showCharacterHasChanged &&
                <div className="absolute top-2 w-full flex items-center">
                  <p className="text-blue mx-auto">Character has been changed</p>
                </div>
              }
            </div>
          </div>
        </UserMenu>
        {
          showLicense &&
          <div className="absolute left-0 top-0 w-screen h-screen bg-dark/35 flex items-center justify-center align-middle" onClick={() => setShowLicense(false)}>
            <div className="w-[75%] h-[87%] mt-14 z-50 flex items-center justify-center relative">
              <LicenseAgreement hasAction={false} onAccept={onLicenseAccept} mode="full" licenseAgreementConfig={licenseAgreementConfig} />
              <button
                className="cursor-pointer bg-blue rounded-md p-1 h-min absolute top-3 right-6"
                onClick={onLicenseAccept}
              >
                <FiX size={20} />
              </button>
            </div>
          </div>
        }
        <SideMenu
          closeMenu={() => setShowMenu(false)}
          showSideMenu={showSideMenu}
          navLinks={navLinks}
          hooks={allHooks}
        />
        {window.vuplex && unityExit &&
          <button
            className="cursor-pointer bg-red rounded-md p-1 ml-2"
            onClick={() => {
              onUnityClose();
            }}
          >
            <FiX size={20} />
          </button>
        }
      </div>
    </div >
  );
}
